import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getAdmins,
  getAllCompanies,
  getCompanyById,
  getConsumersByCompId,
  getDiscomsByCompId,
  getGeneratorsByCompId,
  getMetrics,
  getFullConsumerDetails,
  getFullGeneratorDetails,
  getAllTraders,
  getAdminConsumerCapacityListing,
  getAdminGeneratorCapacityListing,
  getAdminConsumerRaListing,
  getAdminGeneratorRaListing,
  getListingTermsConfiguration,
  getWalletAccountActivity,
} from "../../Services/GNA/GetData";

// --- State Management Utility Function ---

/**
 * Utility to create the boilerplate extraReducers for an async thunk.
 * @param {object} builder - The Redux Toolkit ActionReducerMapBuilder.
 * @param {object} thunk - The createAsyncThunk action.
 * @param {string} stateKey - The key in the state where the fulfilled data should be stored.
 * @param {boolean} [isList=false] - If true, assumes the payload data is a list (stores data: data?.data?.data).
 */
const addAsyncCase = (builder, thunk, stateKey) => {
  builder
    .addCase(thunk.pending, (state) => {
      // Optional: Handle loading state if needed, e.g., state[stateKey + 'Status'] = 'loading'
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[stateKey] = action.payload; // action.payload already contains { data: ... }
    })
    .addCase(thunk.rejected, (state, action) => {
      // Optional: Handle error state if needed
    });
};

// --- Initial State ---

const initialState = {
  fetchCompany: null,
  fetchMetrices: null,
  fetchAdmins: null,
  fetchCompanyById: null,
  fetchConsumersCompanyId: null,
  fetchGeneratorsCompanyId: null,
  fetchDiscomssCompanyId: null,
  fetchFullConsumerDetails: null,
  fetchFullGeneratorDetails: null,
  fetchFullTrader: null,
  fetchCapacity: null, // Unified state for Consumer and Generator Capacities
  fetchRaList: null, // Unified state for Consumer and Generator RA Listings
  activities: {
    data: null,
    loading: false,
    error: null,
  },
};

// --- Async Thunks: Admin & Metrics ---
export const fetchMetrices = createAsyncThunk("metrices", async () => {
  try {
    let data = await getMetrics();
    return { data: data?.data?.data };
  } catch (err) {}
});

export const fetchAdmins = createAsyncThunk("admins", async () => {
  try {
    let data = await getAdmins();
    return { data: data?.data?.data };
  } catch (err) {}
});

// --- Async Thunks: Company Management ---
export const fetchCompany = createAsyncThunk(
  "companyList/fetchCompany",
  async () => {
    try {
      let data = await getAllCompanies();
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

export const fetchCompanyById = createAsyncThunk("company/id", async (id) => {
  try {
    let data = await getCompanyById(id);
    return { data: data?.data };
  } catch (err) {}
});

// --- Async Thunks: Company Sub-Entities (Consumers, Generators, Discoms) ---
export const fetchConsumersCompanyId = createAsyncThunk(
  "company/cons/id",
  async (id) => {
    try {
      let data = await getConsumersByCompId(id);
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

export const fetchGeneratorsCompanyId = createAsyncThunk(
  "company/gens/id",
  async (id) => {
    try {
      let data = await getGeneratorsByCompId(id);
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

export const fetchDiscomssCompanyId = createAsyncThunk(
  "company/dis/id",
  async (id) => {
    try {
      let data = await getDiscomsByCompId(id);
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

// --- Async Thunks: Detailed Entity Views ---
export const fetchFullConsumerDetails = createAsyncThunk(
  "consumer/fullDetails",
  async (consumerId) => {
    try {
      let data = await getFullConsumerDetails(consumerId);
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

export const fetchFullGeneratorDetails = createAsyncThunk(
  "generator/fullDetails",
  async (generatorId) => {
    try {
      let data = await getFullGeneratorDetails(generatorId);
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

export const fetchFullTrader = createAsyncThunk(
  "generator/traders",
  async () => {
    try {
      let data = await getAllTraders();
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

// --- Async Thunks: Capacity Listings (Unified State Key: fetchCapacity) ---
export const fetchConsumerCapacities = createAsyncThunk(
  "consumer/capacities",
  async (id) => {
    try {
      let data = await getAdminConsumerCapacityListing(id);
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

export const fetchGeneratorCapacities = createAsyncThunk(
  "Generator/capacities",
  async (id) => {
    try {
      let data = await getAdminGeneratorCapacityListing(id);
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

// --- Async Thunks: RA Listings (Unified State Key: fetchRaList) ---
export const fetchAdminConsumerRaListing = createAsyncThunk(
  "ra/consumer/capacities",
  async ({ id, type }) => {
    try {
      let data = await getAdminConsumerRaListing({ id, type });
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

export const fetchAdminGeneratorRaListing = createAsyncThunk(
  "ra/Generator/capacities",
  async ({ id, type, companyId }) => {
    try {
      let data = await getAdminGeneratorRaListing({ id, type, companyId });
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

export const fetchListingTermsConfiguration = createAsyncThunk(
  "get/lising view",
  async ({ id, userId, type }) => {
    try {
      let data = await getListingTermsConfiguration({
        id,
        userId: userId ? `userId=${userId}` : "",
        type,
      });
      return { data: data?.data?.data };
    } catch (err) {}
  },
);

export const fetchWalletActivities = createAsyncThunk(
  "wallet/activities",
  async ({ walletId }, { rejectWithValue }) => {
    try {
      let res = await getWalletAccountActivity({ walletId });


      return {
        data: res?.data?.data?.page,
        totalPages: res?.data?.data?.totalPages,
        totalElements: res?.data?.data?.totalElements,
        currentPage: res?.data?.data?.currentPage,
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch activities",
      );
    }
  },
);

// --- Redux Slice Definition ---
const gnaAdminSlice = createSlice({
  name: "gnaAdmin",
  initialState,
  reducers: {
    // Standard reducers for synchronous state updates go here
    clearActivities: (state) => {
      state.activities = {
        data: null,
        loading: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    // Admin & Metrics
    addAsyncCase(builder, fetchCompany, "fetchCompany");
    addAsyncCase(builder, fetchMetrices, "fetchMetrices");
    addAsyncCase(builder, fetchAdmins, "fetchAdmins");
    addAsyncCase(builder, fetchCompanyById, "fetchCompanyById");

    // Company Sub-Entities
    addAsyncCase(builder, fetchConsumersCompanyId, "fetchConsumersCompanyId");
    addAsyncCase(builder, fetchGeneratorsCompanyId, "fetchGeneratorsCompanyId");
    addAsyncCase(builder, fetchDiscomssCompanyId, "fetchDiscomssCompanyId");

    // Detailed Entity Views & Traders
    addAsyncCase(builder, fetchFullConsumerDetails, "fetchFullConsumerDetails");
    addAsyncCase(
      builder,
      fetchFullGeneratorDetails,
      "fetchFullGeneratorDetails",
    );
    addAsyncCase(builder, fetchFullTrader, "fetchFullTrader");

    // Capacity Listings (Unified state key: fetchCapacity)
    addAsyncCase(builder, fetchConsumerCapacities, "fetchCapacity");
    addAsyncCase(builder, fetchGeneratorCapacities, "fetchCapacity");

    // RA Listings (Unified state key: fetchRaList)
    addAsyncCase(builder, fetchAdminGeneratorRaListing, "fetchRaList");
    addAsyncCase(builder, fetchAdminConsumerRaListing, "fetchRaList");
    addAsyncCase(
      builder,
      fetchListingTermsConfiguration,
      "fetchListingTermsConfiguration",
    );

    builder
      .addCase(fetchWalletActivities.pending, (state) => {
        state.activities.loading = true;
        state.activities.error = null;
      })
      .addCase(fetchWalletActivities.fulfilled, (state, action) => {
        state.activities.loading = false;
        state.activities.data = action.payload.data;
        state.activities.totalPages = action.payload.totalPages;
        state.activities.totalElements = action.payload.totalElements;
        state.activities.currentPage = action.payload.currentPage;
      })
      .addCase(fetchWalletActivities.rejected, (state, action) => {
        state.activities.loading = false;
        state.activities.error = action.payload || action.error.message;
      });
  },
});

export const { clearActivities } = gnaAdminSlice.actions;
export default gnaAdminSlice.reducer;
